import type { AvatarState } from '../types/anna';
import { mix, rgba, type RGB } from './color';
import { NO_LIFE, type LifeFrame } from './annaLife';

const TAU = Math.PI * 2;
const WARM: RGB = [242, 166, 90];
const GREY: RGB = [128, 134, 146];
const WHITE: RGB = [255, 255, 255];

export interface FieldParams {
  radius: number;
  spin: number;
  gather: number;
  brightness: number;
  warmth: number;
  grey: number;
  breathAmp: number;
  breathPeriod: number;
  core: number;
  ring: number;
  shimmer: number;
  threads: number;
  speech: number;
  pulse: number;
  drift: number;
  field: number;
  /** Visible energy: luminous filaments streaming through the field */
  energy: number;
}

const base: FieldParams = {
  radius: 0.62,
  spin: 0.07,
  gather: 1,
  brightness: 0.96,
  warmth: 0,
  grey: 0,
  breathAmp: 0.05,
  breathPeriod: 5,
  core: 0.15,
  ring: 0,
  shimmer: 0,
  threads: 0,
  speech: 0,
  pulse: 0,
  drift: 1,
  field: 0.8,
  energy: 0.6,
};

const KEYS = Object.keys(base) as (keyof FieldParams)[];

export const STATE_PARAMS: Record<AvatarState, FieldParams> = {
  rust: { ...base },
  luistert: { ...base, radius: 0.8, spin: 0.11, gather: 1.05, brightness: 1, ring: 1, core: 0.13, field: 1, breathAmp: 0.015, energy: 0.8 },
  denkt: { ...base, radius: 0.58, spin: 1.0, gather: 0.5, brightness: 1, shimmer: 1, core: 0.13, breathAmp: 0.015, energy: 1 },
  spreekt: { ...base, radius: 0.64, spin: 0.12, gather: 0.95, brightness: 1, speech: 1, core: 0.16, breathAmp: 0.02, energy: 0.9 },
  werkt: { ...base, radius: 0.56, spin: 0.08, gather: 0.85, threads: 1, core: 0.14, energy: 0.65 },
  meldt: { ...base, radius: 0.66, spin: 0.1, brightness: 1, core: 0.16, energy: 0.85 },
  nietStoren: { ...base, radius: 0.18, spin: 0.015, gather: 0.3, brightness: 0.45, warmth: 1, breathAmp: 0.03, breathPeriod: 6, core: 0.06, drift: 0.2, field: 0.12, energy: 0 },
  bezorgd: { ...base, radius: 0.6, spin: 0.05, warmth: 0.8, pulse: 1, breathAmp: 0.03, breathPeriod: 3.5, core: 0.15, energy: 0.45 },
  gestopt: { ...base, radius: 0.02, spin: 0, gather: 0.02, brightness: 0.55, grey: 1, breathAmp: 0, core: 0.04, drift: 0, field: 0, energy: 0 },
  slaapt: { ...base, radius: 0.06, spin: 0, gather: 0.2, brightness: 0.28, warmth: 0.5, grey: 0.3, breathAmp: 0, core: 0.045, drift: 0, field: 0, energy: 0 },
};

export function lerpParams(current: FieldParams, target: FieldParams, k: number) {
  for (const key of KEYS) current[key] += (target[key] - current[key]) * k;
}

export interface Particle {
  theta: number;
  phi: number;
  r: number;
  speed: number;
  phase: number;
  size: number;
  alpha: number;
  trail: boolean;
  /** Sparse, brighter particles on the rim */
  rim: boolean;
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createParticles(count: number, seed = 11): Particle[] {
  const rand = mulberry32(seed);
  // Density falls off from the core outward; a sparse shell of brighter motes marks the rim.
  return Array.from({ length: count }, () => {
    const rim = rand() < 0.07;
    return {
      theta: rand() * TAU,
      phi: Math.acos(2 * rand() - 1),
      r: rim ? 0.9 + rand() * 0.1 : 0.05 + Math.pow(rand(), 1.6) * 0.85,
      // Narrow speed spread: the field drifts as one body instead of shearing into spiral arms
      speed: 0.85 + rand() * 0.3,
      phase: rand() * TAU,
      size: rim ? 1.1 + rand() * 1.1 : 0.4 + rand() * 0.9,
      alpha: rim ? 0.75 + rand() * 0.25 : 0.3 + rand() * 0.5,
      trail: rand() < 0.18,
      rim,
    };
  });
}

/** Simulated voice amplitude (0–1) — stands in for the real mic / TTS level. */
export function simulateVoice(t: number, state: AvatarState): number {
  if (state === 'spreekt') {
    const word = Math.sin(t * 2.1) + Math.sin(t * 3.7) * 0.6 > -0.4 ? 1 : 0.08;
    const syllable = Math.abs(Math.sin(t * 7.3)) * (0.6 + 0.4 * Math.sin(t * 13.1));
    return word * (0.25 + 0.75 * syllable);
  }
  if (state === 'luistert') {
    const phrase = Math.sin(t * 0.9) + Math.sin(t * 1.7) * 0.5 > -0.2 ? 1 : 0.1;
    return phrase * (0.2 + 0.8 * Math.abs(Math.sin(t * 5.1) * Math.sin(t * 2.3 + 1)));
  }
  return 0;
}

export interface ThreadSpec {
  color: RGB;
  speed: number;
}

export interface FrameInput {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  t: number;
  angle: number;
  breathPhase: number;
  p: FieldParams;
  particles: Particle[];
  accent: RGB;
  voice: number;
  wave: number | null;
  parallax: { x: number; y: number };
  reduced: boolean;
  threads: ThreadSpec[];
  /** Micro-behaviours: gaze, blink, sigh, perk, proximity, touch burst */
  life?: LifeFrame;
  /** 0 by day … 1 at night */
  night?: number;
}

export function drawFrame(f: FrameInput) {
  const { ctx, p, t } = f;
  const half = Math.min(f.width, f.height) / 2;
  const scale = half / 160;
  ctx.clearRect(0, 0, f.width, f.height);

  const breathWave = Math.sin(f.breathPhase);
  const breath = f.reduced ? 1 : 1 + p.breathAmp * breathWave;
  const breathAlpha = f.reduced && p.breathAmp > 0 ? 0.82 + 0.18 * breathWave : 1;
  const warnPulse = p.pulse * (0.5 + 0.5 * Math.sin((t / 3.5) * TAU));
  const L = f.life ?? NO_LIFE;
  const night = f.night ?? 0;

  let color = mix(f.accent, WARM, Math.min(1, p.warmth + night * 0.2));
  color = mix(color, GREY, p.grey);
  const bright =
    p.brightness *
    breathAlpha *
    (1 + warnPulse * 0.25) *
    (1 - night * 0.1) *
    (1 + L.prox * 0.14 + L.sigh * 0.06) *
    (1 - L.blink * 0.16);

  const cx = f.width / 2 + f.parallax.x * 3 * scale;
  const cy = f.height / 2 + f.parallax.y * 3 * scale;
  const R = Math.max(
    0.5,
    half *
      p.radius *
      breath *
      (1 + p.ring * f.voice * (f.reduced ? 0 : 0.05)) *
      (1 + L.sigh * 0.035 + L.perk * 0.05 + L.prox * 0.03),
  );

  // Attention: the core and inner swarm shift toward where Anna is "looking"
  const gox = L.gazeX * R * 0.16;
  const goy = L.gazeY * R * 0.12;
  const ccx = cx + gox;
  const ccy = cy + goy;
  const shimmerAmt = p.shimmer + L.burst * 0.9;

  // Faint spherical field (back layer)
  if (p.field > 0.01) {
    const g = ctx.createRadialGradient(cx + gox * 0.25, cy + goy * 0.25, 0, cx, cy, R * 1.25);
    g.addColorStop(0, rgba(color, 0.2 * p.field * bright));
    g.addColorStop(0.55, rgba(color, 0.06 * p.field * bright));
    g.addColorStop(1, rgba(color, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, R * 1.25, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(mix(color, WHITE, 0.4), 0.08 * p.field);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, R * 1.02, 0, TAU);
    ctx.stroke();
  }

  ctx.globalCompositeOperation = 'lighter';

  // Particles + trails (mid layer, depth-weighted)
  const tilt = 0.38;
  const ct = Math.cos(tilt);
  const st = Math.sin(tilt);
  const pc = mix(color, WHITE, 0.28);

  const project = (q: Particle, ang: number) => {
    // Slow currents: a smooth flow field bends each particle's path, so matter streams through the
    // field rather than rotating rigidly like a disc.
    const flow = f.reduced ? 0 : p.drift;
    const th = q.theta + ang * q.speed + flow * 0.45 * Math.sin(q.phi * 2.2 + t * 0.11 + q.phase * 0.3);
    const ph =
      q.phi +
      flow * (0.12 * Math.sin(t * 0.35 + q.phase) + 0.22 * Math.sin(q.theta * 1.7 - t * 0.09 + q.r * 4));
    const shimmer = 1 + shimmerAmt * 0.05 * Math.sin(t * 10 + q.phase * 7);
    const inner = q.r < 0.35;
    const rr = R * q.r * (inner ? 1 - L.blink * 0.22 : p.gather) * shimmer;
    const sp = Math.sin(ph);
    const x = rr * sp * Math.cos(th);
    const y0 = rr * Math.cos(ph);
    const z0 = rr * sp * Math.sin(th);
    const y = y0 * ct - z0 * st;
    const z = y0 * st + z0 * ct;
    const depth = z / R;
    const persp = 1 + depth * 0.16;
    // Particles near the core follow the gaze most; the outer shell lags behind
    const follow = (1 - q.r) * (0.6 + 0.2 * (depth + 1));
    return {
      x: cx + x * persp + f.parallax.x * depth * 5 * scale + gox * follow,
      y: cy + y * persp + f.parallax.y * depth * 5 * scale + goy * follow,
      depth,
    };
  };

  const rimC = mix(color, WHITE, 0.55);
  for (const q of f.particles) {
    const a = project(q, f.angle);
    const d = Math.max(0, Math.min(1, (a.depth + 1) / 2));
    // Depth falls off steeply: the far side of the field is a faint haze, the near side is crisp.
    const depthK = 0.08 + 0.92 * d * d;
    // Inner particles stay soft so the core glow — not stacked dots — carries the light.
    const radial = q.rim ? 1.5 : 0.35 + 0.65 * q.r;
    const alpha = Math.min(1, bright * q.alpha * depthK * radial);
    const s = q.size * scale * (0.45 + 0.7 * d);
    ctx.fillStyle = rgba(q.rim ? rimC : pc, alpha);
    ctx.beginPath();
    ctx.arc(a.x, a.y, s, 0, TAU);
    ctx.fill();
    if (q.trail && !f.reduced && p.spin > 0.3) {
      const b = project(q, f.angle - 0.12 - p.spin * 0.18);
      ctx.strokeStyle = rgba(pc, alpha * 0.42);
      ctx.lineWidth = Math.max(0.5, s * 0.7);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }

  // Currents — slow, sinuous streams of light through the field. The path itself breathes and
  // bends, and only the travelling stretch is bright, so they read as flow, not as orbits.
  if (p.energy > 0.02) {
    const fil = mix(color, WHITE, 0.45);
    const loops = 5;
    const segs = 64;
    for (let i = 0; i < loops; i++) {
      const base =
        R * (0.26 + i * 0.1) * (1 + p.speech * f.voice * (f.reduced ? 0 : 0.14)) * (i < 3 ? 1 : p.gather);
      const incl = 0.5 + i * 0.61;
      const yaw = (f.reduced ? 0 : f.angle * (0.45 + i * 0.12)) + i * 1.07;
      const flowSpeed = 0.22 + i * 0.05 + shimmerAmt * 1.4;
      const lean = 0.65 - i * 0.07;
      const si = Math.sin(incl);
      const ci = Math.cos(incl);
      const sy = Math.sin(yaw);
      const cyw = Math.cos(yaw);
      let px = 0;
      let py = 0;
      for (let k = 0; k <= segs; k++) {
        const u = (k / segs) * TAU;
        const wave = f.reduced ? 0 : 0.18 * Math.sin(2 * u + t * 0.23 + i * 1.9) + 0.07 * Math.sin(5 * u - t * 0.37 + i);
        const rr = base * (1 + wave);
        const x0 = rr * Math.cos(u);
        const yA = rr * Math.sin(u) * si;
        const zA = rr * Math.sin(u) * ci;
        const x1 = x0 * cyw + zA * sy;
        const z1 = -x0 * sy + zA * cyw;
        const y2 = yA * ct - z1 * st;
        const z2 = yA * st + z1 * ct;
        const depth = z2 / R;
        const persp = 1 + depth * 0.16;
        const sx = cx + x1 * persp + gox * lean;
        const sY = cy + y2 * persp + goy * lean;
        if (k > 0) {
          const d = Math.max(0, Math.min(1, (depth / 0.7 + 1) / 2));
          const flow = f.reduced ? 0.3 : Math.pow(0.5 + 0.5 * Math.cos(u - t * flowSpeed - i * 1.3), 4);
          const a = p.energy * bright * (0.01 + 0.22 * flow) * (0.15 + 0.85 * d * d);
          ctx.strokeStyle = rgba(fil, a);
          ctx.lineWidth = Math.max(0.5, (0.35 + 0.9 * flow) * scale * (0.6 + 0.5 * d));
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(sx, sY);
          ctx.stroke();
        }
        px = sx;
        py = sY;
      }
    }
  }

  // Luminous core: one soft, continuous gradient — no hard-edged disk anywhere
  const speechK = 1 + p.speech * f.voice * (f.reduced ? 0 : 0.55);
  const coreR = Math.max(
    1.2,
    half * p.core * breath * speechK * (1 + warnPulse * 0.1) * (1 - L.blink * 0.3) * (1 + Math.max(0, L.perk) * 0.12),
  );
  const coreAlpha = bright * (f.reduced ? 0.8 + p.speech * f.voice * 0.2 : 1) * (1 - L.blink * 0.3);
  const hot = mix(color, WHITE, 0.75 - p.grey * 0.5);

  const bloom = ctx.createRadialGradient(ccx, ccy, coreR, ccx, ccy, coreR * 7);
  bloom.addColorStop(0, rgba(color, 0.16 * coreAlpha));
  bloom.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = bloom;
  ctx.beginPath();
  ctx.arc(ccx, ccy, coreR * 7, 0, TAU);
  ctx.fill();

  const cg = ctx.createRadialGradient(ccx, ccy, 0, ccx, ccy, coreR * 3.6);
  cg.addColorStop(0, rgba(hot, 0.62 * coreAlpha));
  cg.addColorStop(0.12, rgba(mix(color, WHITE, 0.5), 0.46 * coreAlpha));
  cg.addColorStop(0.32, rgba(mix(color, WHITE, 0.2), 0.22 * coreAlpha));
  cg.addColorStop(0.62, rgba(color, 0.07 * coreAlpha));
  cg.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = cg;
  ctx.beginPath();
  ctx.arc(ccx, ccy, coreR * 3.6, 0, TAU);
  ctx.fill();

  // Inner warmth: a small, feathered highlight that leans with the gaze — fades to nothing at its edge
  const ix = ccx + gox * 0.15;
  const iy = ccy + goy * 0.15;
  const ig = ctx.createRadialGradient(ix, iy, 0, ix, iy, coreR * 1.3);
  ig.addColorStop(0, rgba(mix(hot, WHITE, 0.35), 0.38 * coreAlpha));
  ig.addColorStop(0.5, rgba(hot, 0.12 * coreAlpha));
  ig.addColorStop(1, rgba(hot, 0));
  ctx.fillStyle = ig;
  ctx.beginPath();
  ctx.arc(ix, iy, coreR * 1.3, 0, TAU);
  ctx.fill();

  // Listening ring — follows voice level
  if (p.ring > 0.02) {
    const ringColor = mix(color, WHITE, 0.3);
    ctx.lineWidth = 1.4 * scale;
    ctx.strokeStyle = rgba(ringColor, (f.reduced ? 0.35 + 0.5 * f.voice : 0.75) * p.ring);
    ctx.beginPath();
    for (let i = 0; i <= 120; i++) {
      const a = (i / 120) * TAU;
      const wobble = f.reduced
        ? 0
        : f.voice * R * 0.07 * (0.6 * Math.sin(a * 5 + t * 5) + 0.4 * Math.sin(a * 9 - t * 7));
      const r = R * 0.98 + (f.reduced ? 0 : f.voice * R * 0.08) + wobble;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.lineWidth = 1 * scale;
    ctx.strokeStyle = rgba(ringColor, 0.16 * p.ring * (0.4 + f.voice));
    ctx.beginPath();
    ctx.arc(cx, cy, R * 1.12, 0, TAU);
    ctx.stroke();
  }

  // Agent threads — one orbit per active agent, speed = progress rate
  if (p.threads > 0.02) {
    f.threads.forEach((thread, i) => {
      const rx = R * (1.05 + i * 0.11);
      const ry = rx * 0.3;
      const rot = -0.5 + i * 0.62;
      ctx.lineWidth = 1 * scale;
      ctx.strokeStyle = rgba(thread.color, 0.08 * p.threads);
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, rot, 0, TAU);
      ctx.stroke();
      const head = f.reduced ? i * 1.7 : t * thread.speed + i * 1.7;
      const segs = 12;
      const tail = 0.9;
      for (let k = 0; k < segs; k++) {
        const a0 = head - tail + (k * tail) / segs;
        ctx.strokeStyle = rgba(thread.color, p.threads * 0.85 * Math.pow((k + 1) / segs, 1.6));
        ctx.lineWidth = (0.8 + 1.4 * (k / segs)) * scale;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, rot, a0, a0 + tail / segs + 0.01);
        ctx.stroke();
      }
      const hx = cx + rx * Math.cos(head) * Math.cos(rot) - ry * Math.sin(head) * Math.sin(rot);
      const hy = cy + rx * Math.cos(head) * Math.sin(rot) + ry * Math.sin(head) * Math.cos(rot);
      ctx.fillStyle = rgba(mix(thread.color, WHITE, 0.3), p.threads);
      ctx.beginPath();
      ctx.arc(hx, hy, 2.2 * scale, 0, TAU);
      ctx.fill();
    });
  }

  // Proactive wave — one gentle ring outward
  if (f.wave !== null) {
    const w = f.wave;
    const start = R * 0.9;
    const r = f.reduced ? R * 1.05 : start + w * (half * 0.97 - start);
    ctx.strokeStyle = rgba(mix(color, WHITE, 0.3), 0.6 * Math.pow(1 - w, 2));
    ctx.lineWidth = (2.2 * (1 - w) + 0.4) * scale;
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(1, r), 0, TAU);
    ctx.stroke();
  }

  ctx.globalCompositeOperation = 'source-over';
}
